'use client';

import { useMemo } from 'react';
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  GUARDRAIL_PIPELINES,
  type GuardrailFormValues,
  type GuardrailsTarget,
} from '@terra/ui-design-system';
import { terraProxyApiClient } from '../../../clients/testerClient';
import {
  useTestingControlsApplicationScope,
  type TestingControlsApplicationScope,
} from './useTestingControlsApplicationScope';

const APPLICATION_GUARDRAILS_QUERY_KEY = 'application-testing-guardrails';

const getApplicationGuardrailsQueryKey = (applicationId: string) =>
  [APPLICATION_GUARDRAILS_QUERY_KEY, applicationId] as const;

export const applicationGuardrailsQueryOptions = (applicationId: string) =>
  queryOptions({
    queryKey: getApplicationGuardrailsQueryKey(applicationId),
    queryFn: () => terraProxyApiClient.getApplicationGuardrails(applicationId),
  });

type SaveGuardrailInput = {
  guardrailId?: string;
  values: GuardrailFormValues;
};

type ToggleGuardrailInput = {
  guardrailId: string;
  enabled: boolean;
};

const saveApplicationGuardrail = ({
  applicationId,
  guardrailId,
  values,
}: SaveGuardrailInput & { applicationId: string }) =>
  guardrailId
    ? terraProxyApiClient.updateApplicationGuardrail(
        applicationId,
        guardrailId,
        values,
      )
    : terraProxyApiClient.createApplicationGuardrail(applicationId, {
        ...values,
        isDefault: false,
        triggerCount: 0,
      });

const createApplicationGuardrailsTarget = ({
  applicationId,
  targetKind,
  queryClient,
}: TestingControlsApplicationScope & {
  queryClient: QueryClient;
}): GuardrailsTarget => {
  const invalidateGuardrails = () => {
    void queryClient.invalidateQueries({
      queryKey: getApplicationGuardrailsQueryKey(applicationId),
    });
  };

  return {
    targetKind,
    availablePipelines: GUARDRAIL_PIPELINES,
    invalidateGuardrails,
    useGuardrails: () => {
      const { data, isLoading } = useQuery(
        applicationGuardrailsQueryOptions(applicationId),
      );

      return { guardrails: data?.guardrails ?? [], isLoading };
    },
    useSaveGuardrail: () => {
      const mutation = useMutation({
        mutationFn: (input: SaveGuardrailInput) =>
          saveApplicationGuardrail({ ...input, applicationId }),
      });

      return { saveAsync: mutation.mutateAsync };
    },
    useToggleGuardrail: () => {
      const mutation = useMutation({
        mutationFn: ({ guardrailId, enabled }: ToggleGuardrailInput) =>
          terraProxyApiClient.updateApplicationGuardrail(
            applicationId,
            guardrailId,
            { enabled },
          ),
        onSuccess: invalidateGuardrails,
      });

      return {
        toggleAsync: async (input) => {
          await mutation.mutateAsync(input);
        },
      };
    },
    useDeleteGuardrail: () => {
      const mutation = useMutation({
        mutationFn: (guardrailId: string) =>
          terraProxyApiClient.deleteApplicationGuardrail(
            applicationId,
            guardrailId,
          ),
        onSuccess: invalidateGuardrails,
      });

      return {
        isPending: mutation.isPending,
        deleteAsync: async (guardrailId) => {
          await mutation.mutateAsync(guardrailId);
        },
      };
    },
  };
};

export const useApplicationGuardrailsTarget = (): GuardrailsTarget | null => {
  const queryClient = useQueryClient();
  const scope = useTestingControlsApplicationScope();

  return useMemo(
    () =>
      scope
        ? createApplicationGuardrailsTarget({ ...scope, queryClient })
        : null,
    [queryClient, scope],
  );
};
